import { useCallback } from 'react';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
    createPageVersion,
    sendPageVersionForApproval,
    editPageVersion,
    updatePageVersionStatusToDraft as apiUpdateDraft
} from '../../services/pageversionservice';
import {
    CreateVersionValues,
    ApprovalValues,
    EditVersionValues,
} from '../../types/types';

interface UsePageVersionActionsProps {
    userGuid: string;
    id: string;
    pageVersionName: string;
    setPageVersionName: (name: string) => void;
    setAvailablePageVersions: (fn: (prev: any[]) => any[]) => void;
    setHasPageVersionStatusChanged: (val: boolean | ((prev: boolean) => boolean)) => void;
    setHasPageVersionNameChanged: (val: boolean | ((prev: boolean) => boolean)) => void;
    setLoadingInitial: (val: boolean) => void;
    setIsCreateDrawerVisible: (val: boolean) => void;
    setIsPublishDrawerVisible: (val: boolean) => void;
    setIsEditPropDrawerVisible: (val: boolean) => void;
}

export const usePageVersionActions = ({
    userGuid,
    id,
    pageVersionName,
    setPageVersionName,
    setAvailablePageVersions,
    setHasPageVersionStatusChanged,
    setHasPageVersionNameChanged,
    setLoadingInitial,
    setIsCreateDrawerVisible,
    setIsPublishDrawerVisible,
    setIsEditPropDrawerVisible,
}: UsePageVersionActionsProps) => {
    const navigate = useNavigate();

    const handleCreateNewVersion = useCallback(async (values: CreateVersionValues) => {
        setIsCreateDrawerVisible(false);
        try {
            const { id: newPageVersionId } = await createPageVersion(values.name, userGuid);
            setPageVersionName(values.name);
            setAvailablePageVersions(prev => [...prev, { guid: newPageVersionId, name: values.name }]);
            navigate(`/kioskpageversion/${newPageVersionId}`);

            notification.success({
                message: 'Success',
                description: `"${values.name}" PageVersion Created Successfully`,
            });
        } catch (error: any) {
            console.error('Error creating page version:', error);
            let errorMessage = 'Error';
            let errorDescription = 'An unexpected error occurred. Please try again.';

            if (error.response) {
                const { data, status } = error.response;
                if (status === 409 && data?.message) {
                    errorMessage = 'Creation Conflict';
                    errorDescription = data.message;
                } else {
                    errorMessage = `Server Error (${status})`;
                    errorDescription = data?.message || `Server responded with status ${status}.`;
                }
            } else if (error.request) {
                errorMessage = 'Network Error';
                errorDescription = 'No response from server. Please check your internet connection.';
            }

            notification.error({ message: errorMessage, description: errorDescription });
        }
    }, [userGuid, navigate, setPageVersionName, setAvailablePageVersions, setIsCreateDrawerVisible]);

    const handleSendForApproval = useCallback(async (values: ApprovalValues) => {
        setIsPublishDrawerVisible(false);
        setLoadingInitial(true);
        try {
            await sendPageVersionForApproval({
                pageVersionGuid: id,
                createdBy: userGuid,
                selectedStores: values.stores,
                startDate: values.startDate,
                endDate: values.endDate,
                override: values.override ?? false,
                conflicts: values.conflicts || []
            });

            notification.success({
                message: 'Approval Request Sent',
                description: `"${values.name}" PageVersion has been submitted for approval.`,
                showProgress: true,
                pauseOnHover: true,
            });

            setHasPageVersionStatusChanged(prev => !prev);
        } catch (error: any) {
            console.error('Error sending for approval:', error);
            notification.error({
                message: 'Error',
                description: error.response?.data || 'Something went wrong while sending for approval.',
            });
        } finally {
            setLoadingInitial(false);
        }
    }, [id, userGuid, setLoadingInitial, setHasPageVersionStatusChanged, setIsPublishDrawerVisible]);

    const handleEditPageVersionProperties = useCallback(async (values: EditVersionValues) => {
        setIsEditPropDrawerVisible(false);
        setLoadingInitial(true);
        try {
            await editPageVersion(id, values.name);
            setHasPageVersionNameChanged(prev => !prev);

            notification.success({
                message: 'Success',
                description: `"${values.name}" PageVersion Updated Successfully`,
                showProgress: true,
                pauseOnHover: true,
            });
        } catch (error: any) {
            console.error('Error updating page version:', error);
            notification.error({
                message: 'Error',
                description: error.response?.data || 'Failed to update page version.',
            });
        } finally {
            setLoadingInitial(false);
        }
    }, [id, setLoadingInitial, setHasPageVersionNameChanged, setIsEditPropDrawerVisible]);

    const updatePageVersionStatusToDraft = useCallback(async () => {
        setLoadingInitial(true);
        try {
            const { id: newPageVersionId } = await apiUpdateDraft({
                guid: id,
                name: pageVersionName,
                createdBy: userGuid,
            });

            navigate(`/kioskpageversion/${newPageVersionId}`);
            notification.success({
                message: 'Success',
                description: `"${pageVersionName}" PageVersion draft Created Successfully`,
            });
        } catch (error: any) {
            console.error(error);
            notification.error({
                message: `Error creating ${pageVersionName} pageversion draft.`,
                description: error.response?.data || 'Unexpected error occurred.',
            });
        } finally {
            setLoadingInitial(false);
        }
    }, [id, pageVersionName, userGuid, navigate, setLoadingInitial]);

    return {
        handleCreateNewVersion,
        handleSendForApproval,
        handleEditPageVersionProperties,
        updatePageVersionStatusToDraft,
    };
};